export function commercialCustomerKey(item = {}) {
  const uid = String(item.userId || '').trim();
  const email = String(item.email || '').trim();
  // Keep legacy email casing: the adapter queries both this value and lowercase.
  return uid || email ? JSON.stringify([uid, email]) : '';
}

const positiveInteger = (value, fallback) => Number.isFinite(value) && value > 0
  ? Math.max(1, Math.floor(value)) : fallback;
const obsoleteError = () => Object.assign(new Error('Controllo commerciale non più richiesto.'), {
  code: 'commercial-status-obsolete',
});

// Component-local cache and scheduler. No database request starts outside runQuery.
export function createPublicBookingCommercialLoader({
  loadBatch,
  onUpdate,
  onError,
  concurrency = 4,
  batchSize = 10,
  ttlMs = 120000,
  timeoutMs = 20000,
  maxCacheSize = 500,
  now = Date.now,
}) {
  if (typeof loadBatch !== 'function' || typeof onUpdate !== 'function') {
    throw new TypeError('loadBatch e onUpdate sono obbligatori.');
  }
  const queryLimit = Math.min(4, positiveInteger(concurrency, 4));
  const groupSize = positiveInteger(batchSize, 10);
  const queryTimeout = positiveInteger(timeoutMs, 20000);
  const cacheLimit = positiveInteger(maxCacheSize, 500);
  const cacheTtl = Number.isFinite(ttlMs) ? Math.max(0, ttlMs) : 120000;
  const cache = new Map();
  const batches = new Set();
  const jobs = new Set();
  const queue = [];
  let current = new Map();
  let visible = [];
  let physicalQueries = 0;
  let disposed = false;

  const pruneCache = () => {
    const time = now();
    for (const [key, value] of cache) {
      if (value.expiresAt <= time) cache.delete(key);
    }
    while (cache.size > cacheLimit) cache.delete(cache.keys().next().value);
  };

  const publish = () => {
    if (disposed) return;
    const statuses = Object.fromEntries(visible.map(({ id, key }) => [
      id, key ? { ...(current.get(key)?.info || { status: 'loading' }) } : { status: 'unknown' },
    ]));
    // A view callback must not leave the network queue or its timers stuck.
    try {
      onUpdate(statuses);
    } catch (error) {
      console.error('Errore aggiornamento situazione commerciale:', error);
    }
  };

  const isCurrent = (batch, item) => {
    if (disposed || batch.finished) return false;
    const key = item === undefined ? null : commercialCustomerKey(item || {});
    return batch.records.some((record) => (key === null || record.key === key)
      && current.get(record.key) === record);
  };

  const settleJob = (job, error, value) => {
    if (job.done) return;
    job.done = true;
    clearTimeout(job.timer);
    jobs.delete(job);
    if (!job.started) {
      const index = queue.indexOf(job);
      if (index !== -1) queue.splice(index, 1);
    }
    if (error) job.reject(error);
    else job.resolve(value);
  };

  const cancelBatchJobs = (batch, error) => {
    for (const job of jobs) {
      if (job.batch === batch) settleJob(job, error);
    }
  };

  const completeBatch = (batch, results, error = null) => {
    if (batch.finished) return;
    batch.finished = true;
    batches.delete(batch);
    cancelBatchJobs(batch, error || obsoleteError());
    if (disposed) return;
    let changed = false;
    for (const record of batch.records) {
      if (current.get(record.key) !== record) continue;
      const info = !error && results?.get(record.key);
      record.info = info && typeof info.status === 'string' && info.status !== 'loading'
        ? { ...info } : { status: 'error', errorCode: String(error?.code || 'commercial-status-failed') };
      record.pending = false;
      if (record.info.status !== 'error' && cacheTtl > 0) {
        cache.delete(record.key);
        cache.set(record.key, { info: { ...record.info }, expiresAt: now() + cacheTtl });
      }
      changed = true;
    }
    pruneCache();
    if (changed) publish();
    if (changed && error && typeof onError === 'function') {
      try {
        onError(error);
      } catch (callbackError) {
        console.error('Errore diagnostica situazione commerciale:', callbackError);
      }
    }
  };

  const armJobTimeout = (job) => {
    clearTimeout(job.timer);
    job.timer = setTimeout(() => {
      const error = Object.assign(new Error('Tempo di attesa del controllo commerciale superato.'), {
        code: 'commercial-status-timeout',
      });
      settleJob(job, error);
      completeBatch(job.batch, null, error);
      drain();
    }, queryTimeout);
  };

  const drain = () => {
    while (!disposed && physicalQueries < queryLimit && queue.length) {
      const job = queue.shift();
      if (job.done) continue;
      if (!isCurrent(job.batch)) {
        settleJob(job, obsoleteError());
        continue;
      }
      job.started = true;
      // The active read gets its full deadline, independently of queue time.
      armJobTimeout(job);
      physicalQueries += 1;
      Promise.resolve().then(() => {
        if (job.done || !isCurrent(job.batch)) throw obsoleteError();
        return job.operation();
      }).then((value) => {
        settleJob(job, null, value);
      }, (error) => {
        settleJob(job, error);
        completeBatch(job.batch, null, error);
      }).finally(() => {
        // Firestore cannot abort a started read. A logical timeout does NOT free
        // this slot: otherwise repeated navigation could exceed the physical cap.
        physicalQueries -= 1;
        // Healthy earlier reads may take more than one timeout interval in total.
        // Queued reads expire only if the queue stops making progress.
        for (const waiting of queue) {
          if (!waiting.done) armJobTimeout(waiting);
        }
        drain();
      });
    }
  };

  const runQuery = (batch, operation) => {
    if (!isCurrent(batch)) return Promise.reject(obsoleteError());
    if (typeof operation !== 'function') return Promise.reject(new TypeError('Query non valida.'));
    return new Promise((resolve, reject) => {
      const job = { batch, operation, resolve, reject, started: false, done: false, timer: null };
      // Keep a queue watchdog too: a previous timed-out physical request may
      // still be unresolved, so waiting rows must not spin indefinitely.
      armJobTimeout(job);
      jobs.add(job);
      queue.push(job);
      drain();
    });
  };

  const startBatch = (records) => {
    const batch = { records, finished: false };
    batches.add(batch);
    Promise.resolve().then(() => {
      if (!isCurrent(batch)) return null;
      return loadBatch(records.map((record) => record.item), {
        runQuery: (operation) => runQuery(batch, operation),
        // Queued reads can narrow their identities immediately before execution.
        isCurrent: (item) => isCurrent(batch, item),
      });
    }).then((results) => {
      if (!isCurrent(batch)) return;
      if (!results || typeof results.get !== 'function') {
        throw new TypeError('loadBatch deve restituire una Map di stati commerciali.');
      }
      completeBatch(batch, results);
    }).catch((error) => completeBatch(batch, null, error));
  };

  const select = (items = [], { force = false } = {}) => {
    if (disposed) return;
    pruneCache();
    const next = new Map();
    const newRecords = [];
    visible = [];
    for (const item of items) {
      if (!item?.id) continue;
      const key = commercialCustomerKey(item);
      visible.push({ id: item.id, key });
      if (!key || next.has(key)) continue;
      if (force) cache.delete(key);
      const previous = current.get(key);
      if (!force && previous?.pending) {
        next.set(key, previous);
        continue;
      }
      const cached = !force && cache.get(key);
      if (cached) {
        // Refresh recency, not expiry: a frequently opened customer still refreshes.
        cache.delete(key);
        cache.set(key, cached);
        next.set(key, { key, info: { ...cached.info }, pending: false });
        continue;
      }
      const record = {
        key,
        item: { ...item, userId: String(item.userId || '').trim(), email: String(item.email || '').trim() },
        info: { status: 'loading' },
        pending: true,
      };
      next.set(key, record);
      newRecords.push(record);
    }
    current = next;
    for (const batch of batches) {
      if (!isCurrent(batch)) {
        batch.finished = true;
        batches.delete(batch);
        cancelBatchJobs(batch, obsoleteError());
      }
    }
    publish();
    for (let index = 0; index < newRecords.length; index += groupSize) {
      startBatch(newRecords.slice(index, index + groupSize));
    }
    drain();
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    visible = [];
    current.clear();
    cache.clear();
    for (const batch of batches) {
      batch.finished = true;
      cancelBatchJobs(batch, obsoleteError());
    }
    batches.clear();
  };

  return { select, dispose };
}
